import { useMemo } from 'react';
import { PetDetails } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PersonTypes, PersonHelpers } from '@frontend/api-person';
import { useMediaMatches } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { ResponsivePetChips } from '@frontend/vet-components';
import { theme } from '@frontend/theme';
import { ListRow, SkeletonLoader } from '@frontend/design-system';
import { Expandable, ContactListRowWrapper, Actions } from './contact-list-row';

export const VetContactListRow = ({
  listItem,
  children,
  getLocationsByDatasource,
  isLoading,
  showNameAsFirstandLastName,
}: {
  isLoading: boolean;
  listItem: PersonTypes.Person & { pets: PetDetails[] };
  children?: React.ReactNode;
  showNameAsFirstandLastName?: boolean;
  getLocationsByDatasource: (
    sourceId: string,
    clientLocationId?: string | null | undefined
  ) => DataSourcesHooks.LocationReference[];
}) => {
  const { matches } = useMediaMatches();
  const { locationData, selectedOrgId } = useAppScopeStore();
  const { SourceID = '', ClientLocationID, pets, FirstName = '', LastName = '' } = listItem || {};
  const isDemoLocation = locationData?.[selectedOrgId]?.opsType === 'DEMO';
  const fullName = showNameAsFirstandLastName
    ? `${FirstName.trim()} ${LastName}`.trim()
    : PersonHelpers.getFullName(listItem);
  const isMobileScreen = matches.useXSmallMax();
  const locations = getLocationsByDatasource(SourceID, ClientLocationID);
  const activePets = useMemo(
    () => pets.filter((pet) => pet.activeStatus === 'ACTIVE' || pet.activeStatus === 'UNKNOWN'),
    []
  );

  return (
    <ContactListRowWrapper listItem={listItem} locations={locations}>
      <ListRow.Content
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div
          style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', gap: theme.spacing(0.5) }}
        >
          <ListRow.Content.Title>{fullName}</ListRow.Content.Title>
          {!pets?.length && isLoading ? <SkeletonLoader height={24} width={80} /> : null}
          {pets?.length ? <ResponsivePetChips pets={!isDemoLocation ? activePets : pets} /> : null}
        </div>
      </ListRow.Content>
      {isMobileScreen ? (
        <Expandable>
          <Actions person={listItem} getLocationsByDatasource={getLocationsByDatasource} />
        </Expandable>
      ) : (
        <ListRow.Trail css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              paddingRight: theme.spacing(2),
            }}
          >
            {children}
          </div>
          <Actions person={listItem} getLocationsByDatasource={getLocationsByDatasource} />
        </ListRow.Trail>
      )}
    </ContactListRowWrapper>
  );
};
